import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import {
  ClipboardList, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle, Plus, ListChecks 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useRequisitions,
  useCreateRequisition,
  RequisitionCard,
  RequisitionDetailModal,
  CreateRequisitionModal,
} from '@/features/requisitions';
import type {
  RequisitionStatus, Requisition, CreateRequisitionLine 
} from '@/features/requisitions';
import { PickListsView } from './requisitions/pick-lists-view';
import { toast } from 'sonner';

const DEFAULT_SITE_ID = 'site-celebration';

const TABS = [
  {
    id: 'create',
    label: 'Create Request',
    href: '/requisitions/create',
    icon: Plus 
  },
  {
    id: 'pending',
    label: 'Pending Approval',
    href: '/requisitions/pending',
    icon: Clock 
  },
  {
    id: 'picks',
    label: 'Pick Lists',
    href: '/requisitions/picks',
    icon: ListChecks 
  },
];

const STATUS_TABS: {
  value: RequisitionStatus | undefined;
  label: string;
  icon: typeof ClipboardList 
}[] = [
  {
    value: undefined,
    label: 'All',
    icon: ClipboardList 
  },
  {
    value: 'pending',
    label: 'Pending',
    icon: Clock 
  },
  {
    value: 'approved',
    label: 'Approved',
    icon: CheckCircle 
  },
  {
    value: 'fulfilled',
    label: 'Fulfilled',
    icon: CheckCircle 
  },
  {
    value: 'partial',
    label: 'Partial',
    icon: AlertCircle 
  },
  {
    value: 'rejected',
    label: 'Rejected',
    icon: XCircle 
  },
];

export function RequisitionsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<RequisitionStatus | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | null>(null);

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/create')) return 'create';
    if (location.pathname.includes('/pending')) return 'pending';
    if (location.pathname.includes('/picks')) return 'picks';
    return 'pending';
  }, [location.pathname]);

  // Auto-filter by status based on tab
  const effectiveStatus = useMemo(() => {
    if (activeTab === 'pending') return 'pending' as const;
    return selectedStatus;
  }, [activeTab, selectedStatus]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useRequisitions(DEFAULT_SITE_ID, { status: effectiveStatus });
  const { data: allData } = useRequisitions(DEFAULT_SITE_ID);

  const createRequisitionMutation = useCreateRequisition({
    siteId: DEFAULT_SITE_ID,
    onSuccess: () => {
      toast.success('Requisition created successfully');
      setShowAddModal(false);
      refetch();
    },
    onError: (err) => {
      toast.error(`Failed to create requisition: ${err.message}`);
    },
  });

  const requisitions = data?.data ?? [];

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    for (const req of allData?.data ?? []) {
      counts.all++;
      counts[req.status] = (counts[req.status] ?? 0) + 1;
    }
    return counts;
  }, [allData]);

  const handleAddRequisition = () => {
    setShowAddModal(true);
  };

  const handleCreateRequisition = (data: {
    venueId: string;
    venueName: string;
    neededBy: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    notes: string;
    items: CreateRequisitionLine[];
  }) => {
    createRequisitionMutation.mutate({
      venueId: data.venueId,
      venueName: data.venueName,
      neededBy: data.neededBy,
      priority: data.priority,
      notes: data.notes,
      items: data.items,
    });
  };

  const handleOpenRequisition = (requisition: Requisition) => {
    setSelectedRequisition(requisition);
  };

  const handleCloseRequisition = () => {
    setSelectedRequisition(null);
  };

  const handleApproveRequisition = (requisitionId: string) => {
    toast.success(`Requisition ${requisitionId} approved`);
    setSelectedRequisition(null);
    refetch();
  };

  const handleRejectRequisition = (requisitionId: string) => {
    toast.success(`Requisition ${requisitionId} rejected`);
    setSelectedRequisition(null);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported requisitions');
      refetch();
    }, 1500);
  };

  const exportData = requisitions.map((r) => ({
    requisitionId: r.requisitionId,
    venue: r.venueName,
    status: r.status,
    totalItems: r.totalItems,
    neededBy: r.neededBy,
    requestedBy: r.requestedByName,
    createdAt: r.createdAt,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Requisitions</h1>
          <p className="text-muted-foreground">Manage internal stock requests from venues</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'create' && (
        <>
          <CrudToolbar
            entityName="Requisition"
            entityNamePlural="Requisitions"
            onAdd={handleAddRequisition}
            canEdit={false}
            canDelete={false}
            canBulkUpload={false}
            canExport={false}
          />

          <CreateRequisitionModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleCreateRequisition}
            isSubmitting={createRequisitionMutation.isPending}
            siteId={DEFAULT_SITE_ID}
          />

          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Plus className="mb-4 h-12 w-12 text-muted-foreground" />
              <p className="text-lg font-medium">Create New Requisition</p>
              <p className="text-sm text-muted-foreground">Request stock from central storage for your venue</p>
              <Button className="mt-4" onClick={handleAddRequisition}>Start Request</Button>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'picks' && (
        <PickListsView
          requisitions={(allData?.data ?? []).filter((r) => r.status === 'approved')}
          isLoading={isLoading}
          error={error}
          onRefetch={refetch}
          onFulfill={(requisitionId) => {
            toast.success(`Requisition ${requisitionId} marked as fulfilled`);
            refetch();
          }}
        />
      )}

      {activeTab === 'pending' && (
        <>
          <CrudToolbar
            entityName="Requisition"
            entityNamePlural="Requisitions"
            onAdd={handleAddRequisition}
            onBulkUpload={handleBulkUpload}
            exportData={exportData}
            exportFilename="requisitions.csv"
            canEdit={false}
            canDelete={false}
          />

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Requests</CardTitle>
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold">{statusCounts.all}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending Approval</CardTitle>
                <Clock className="h-5 w-5 text-amber-500" />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-amber-600">{statusCounts.pending ?? 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Approved</CardTitle>
                <CheckCircle className="h-5 w-5 text-blue-500" />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-blue-600">{statusCounts.approved ?? 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Fulfilled</CardTitle>
                <CheckCircle className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-green-600">{statusCounts.fulfilled ?? 0}</div></CardContent>
            </Card>
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => {
              const Icon = tab.icon;
              const count = tab.value ? statusCounts[tab.value] ?? 0 : statusCounts.all;
              return (
                <Button key={tab.label} variant={selectedStatus === tab.value ? 'default' : 'outline'} size="sm" onClick={() => setSelectedStatus(tab.value)}>
                  <Icon className="mr-2 h-4 w-4" />
                  {tab.label} ({count})
                </Button>
              );
            })}
          </div>

          {isLoading && (
            <div className="grid gap-4 md:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <div key={`skel-req-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />)}</div>
          )}
          {!isLoading && error && (
            <Card><CardContent className="flex flex-col items-center justify-center py-12">
              <ClipboardList className="mb-4 h-12 w-12 text-red-500" />
              <p className="text-lg font-medium">Failed to load requisitions</p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()}>Try Again</Button>
            </CardContent></Card>
          )}
          {!isLoading && !error && requisitions.length === 0 && (
            <Card><CardContent className="flex flex-col items-center justify-center py-12">
              <ClipboardList className="mb-4 h-12 w-12 text-muted-foreground" />
              <p className="text-lg font-medium">No requisitions found</p>
            </CardContent></Card>
          )}
          {!isLoading && !error && requisitions.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2">
              {requisitions.map((req) => (
                <RequisitionCard
                  key={req.requisitionId}
                  requisition={req}
                  onClick={() => handleOpenRequisition(req)}
                />
              ))}
            </div>
          )}

          <RequisitionDetailModal
            requisition={selectedRequisition}
            isOpen={selectedRequisition !== null}
            onClose={handleCloseRequisition}
            onApprove={handleApproveRequisition}
            onReject={handleRejectRequisition}
          />
        </>
      )}
    </div>
  );
}
